import { identity, isNil } from "ramda";
import { styled } from "styled-components";

import { isPresentObject } from "lib/helpers";
import { BreakpointName, breakpointsAscending, Theme } from "lib/theme";

const cols = {
  EQUAL: "repeat(auto-fit, minmax(0, 1fr))",
};

type GridCols = string | (number | string)[];
export type GridBreakpoints = Partial<Record<BreakpointName, GridCols>>;

const getStyle = (cols: string): string => `grid-template-columns: ${cols};`;

const generateGridColumns = (cols?: GridCols): string | undefined => {
  if (isNil(cols)) return;
  if (typeof cols === "string") return getStyle(cols);
  return getStyle(cols.map((col) => (typeof col === "number" ? `minmax(0, ${col}fr)` : col)).join(" "));
};

export const colsWithBreakpoints = ({
  theme,
  $cols,
}: {
  theme: Theme;
  $cols?: GridBreakpoints | GridCols;
}): string | undefined => {
  if (isPresentObject<GridBreakpoints>($cols)) {
    /* If there are multiple breakpoints specified, it is necessary to put rules in correct order (from narrowest
     * breakpoint to widest) as min-width selector is used and particular screen width will match all the breakpoints
     * that are narrower. For example, if there are rules for xs (min-width: 0px) and for md (min-width: 769px)
     * and the screen is 1024px, rules must be specified in this exact order (xs, md) or md will never apply.
     *
     * JS objects iterate keys in predictable way, but this way doesn't always match the assignment order (or
     * order in which keys are specified when constant is declared), so use explicitly ordered array instead). */
    return breakpointsAscending
      .map((bkName) => {
        const bkCols = $cols[bkName];
        if (isNil(bkCols)) return;
        return `${theme.breakpoints.up(bkName).replace("@media ", "@media screen and ")}{${generateGridColumns(bkCols)}}`;
      })
      .filter(identity)
      .join("\n");
  }
  return generateGridColumns($cols);
};

const GridComponent = styled.div<{
  $cols?: GridBreakpoints | GridCols;
  $tight?: boolean | "extra";
}>`
  display: grid;
  ${colsWithBreakpoints};
  grid-gap: ${({ $tight }) => ($tight ? ($tight === "extra" ? "0" : "1") : "2")}rem;
  grid-auto-columns: minmax(min-content, 1fr);
`;

type GridType = typeof GridComponent & { cols: typeof cols };
export const Grid: GridType = GridComponent as GridType;
Grid.cols = cols;
